import { QUOTES_WATCHLIST_STORAGE_KEYS } from '@/lib/localStorageKeys/quotes-watchlist';
import { DEFAULT_QUOTES_WATCHLIST } from './default-data';
import { QuotesWatchlist } from './types';
import { writeEssential } from '@/lib/storage/safeLocalStorage';

// New functions for symbol keys
export const getStoredSymbolKeys = (): string[] => {
  if (typeof window === 'undefined') {
    return DEFAULT_QUOTES_WATCHLIST.map(item => item.symbolKey);
  }

  try {
    const storedData = localStorage.getItem(QUOTES_WATCHLIST_STORAGE_KEYS.CORE);
    
    if (!storedData) {
      // If no data exists, save defaults and return default keys
      const defaultKeys = DEFAULT_QUOTES_WATCHLIST.map(item => item.symbolKey);
      saveSymbolKeys(defaultKeys);
      return defaultKeys;
    }

    const parsedData = JSON.parse(storedData);
    
    // Handle legacy format (array of WatchlistItem objects)
    if (Array.isArray(parsedData)) {
      if (parsedData.length > 0 && typeof parsedData[0] === 'object' && 'symbolKey' in parsedData[0]) {
        // Convert legacy format to new format
        const keys = parsedData.map((item: any) => item.symbolKey).filter(Boolean);
        saveSymbolKeys(keys);
        return keys;
      }
      // Already in new format (array of strings)
      return parsedData.filter(item => typeof item === 'string');
    }

    console.error('Invalid watchlist data in localStorage');
    const defaultKeys = DEFAULT_QUOTES_WATCHLIST.map(item => item.symbolKey);
    saveSymbolKeys(defaultKeys);
    return defaultKeys;
  } catch (error) {
    console.error('Error loading symbol keys from localStorage:', error);
    return DEFAULT_QUOTES_WATCHLIST.map(item => item.symbolKey);
  }
};

export const saveSymbolKeys = (symbolKeys: string[]): void => {
  if (typeof window === 'undefined') {
    return;
  }

  // The watchlist is the user's own list, not a re-fetchable cache: it must not be the write that loses
  // when the origin is full (see safeLocalStorage).
  writeEssential(QUOTES_WATCHLIST_STORAGE_KEYS.CORE, JSON.stringify(symbolKeys));
};

/**
 * Digits per saved symbol, so `loadWatchlist` can render rows before the symbol catalog has streamed in.
 * Missing or stale entries are harmless — the catalog overwrites them the moment it resolves a symbol.
 */
export const getStoredSymbolDigits = (): Record<string, number> => {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem(QUOTES_WATCHLIST_STORAGE_KEYS.DIGITS);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: Record<string, number> = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof key === 'string' && typeof value === 'number' && Number.isFinite(value)) out[key] = value;
    }
    return out;
  } catch (error) {
    console.error('Error loading watchlist digits from localStorage:', error);
    return {};
  }
};

export const saveSymbolDigits = (digitsByKey: Record<string, number>): void => {
  if (typeof window === 'undefined') return;
  writeEssential(QUOTES_WATCHLIST_STORAGE_KEYS.DIGITS, JSON.stringify(digitsByKey));
};

// Keep old functions for backward compatibility but mark as deprecated
/**
 * @deprecated Use getStoredSymbolKeys instead
 */
export const getStoredQuotesWatchlist = (): QuotesWatchlist => {
  const keys = getStoredSymbolKeys();
  return keys.map(key => ({ symbolKey: key, digits: 5 })); // Default digits
};

/**
 * @deprecated Use saveSymbolKeys instead
 */
export const saveQuotesWatchlist = (watchlist: QuotesWatchlist): void => {
  const keys = watchlist.map(item => item.symbolKey);
  saveSymbolKeys(keys);
};

/**
 * Get the size of stored quotes watchlist in bytes
 * @returns Size in bytes or 0 if not found
 */
export function getStoredQuotesWatchlistValue(): number {
  if (typeof window === 'undefined') return 0;
  
  try {
    const stored = localStorage.getItem(QUOTES_WATCHLIST_STORAGE_KEYS.CORE);
    
    if (!stored) return 0;
    
    // Calculate size in bytes (JavaScript strings are UTF-16, so each character is 2 bytes)
    return stored.length * 2;
  } catch (error) {
    console.error('Error getting quotes watchlist size:', error);
    return 0;
  }
}

/**
 * Clear stored quotes watchlist
 * @returns Success status
 */
export function clearStoredQuotesWatchlist(): boolean {
  if (typeof window === 'undefined') return false;
  
  try {
    localStorage.removeItem(QUOTES_WATCHLIST_STORAGE_KEYS.CORE);
    return true;
  } catch (error) {
    console.error('Error clearing quotes watchlist:', error);
    return false;
  }
}